import Client from "./base/client";

export class CheckoutPayment {
  private client: Client;

  constructor(client: Client) {
    this.client = client;
  }

  private resourceUrl = "/api/v2/checkout";

  fetchDetail = <TData extends object>({
    form_id,
    registration,
  }: {
    form_id: string;
    registration: string;
  }): Promise<TData> => {
    const formData = new FormData();
    formData.append("form_id", form_id);
    formData.append("registration", registration);

    return this.client.post(`${this.resourceUrl}/detail`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  completeCheckoutPayment = <TData extends object>(
    data: any
  ): Promise<TData> => {
    return this.client.post(`${this.resourceUrl}/complete`, data);
  };

  // Apple Pay: ask our backend for an Opayo-issued merchant session.
  // formId keys the SessionValidationToken server-side to this checkout; without
  // it the backend falls back to a single shared slot, which is fine for one
  // tester and wrong for concurrent shoppers.
  validateAppleMerchant = <TData extends object>(
    validationUrl: string,
    domainName: string,
    formId?: string
  ): Promise<TData> => {
    return this.client.post(`${this.resourceUrl}/applepay-session`, {
      validationUrl,
      domainName,
      formId,
    });
  };

  // Apple Pay: submit encrypted wallet token after onpaymentauthorized
  // network and cardClass are REQUIRED, not optional extras. The server reads
  // them (CheckoutReviewController applePayCharge) and feeds them to
  // OpayoWalletPaymentService::deriveCardType(), which maps Apple's network+type
  // onto Opayo's CardType — Visa+debit->DELTA, Mastercard+debit->MCDEBIT and so
  // on, per Elavon's Apple Pay documentation.
  //
  // They were never sent. deriveCardType('','') returns '' and logs
  // "UNKNOWN CARD NETWORK", so every Apple Pay charge went out with an empty
  // CardType. Found 2026-10-01 while fixing the payload bug below, by comparing
  // this path against the partner page that had just taken a real live payment.
  submitApplePayCharge = <TData extends object>(
    formId: string,
    walletToken: string,
    amount: string,
    network: string = "",
    cardClass: string = "",
    plateFixtures: string = "",
    plateFixturesValue: number = 0
  ): Promise<TData> => {
    return this.client.post(`${this.resourceUrl}/applepay-charge`, {
      formId,
      walletToken,
      amount,
      network,
      cardClass,
      plateFixtures,
      plateFixturesValue,
    });
  };

  validateDiscountCode = <TData extends object>(
    registration: string,
    discountCode: string,
    email: string = "",
    hasFinance: boolean = false,
    hasPlates: boolean = false,
    hasTransfer: boolean = false
  ): Promise<TData> => {
    return this.client.post(`${this.resourceUrl}/validate-discount`, {
      registration,
      discount_code:  discountCode,
      email,
      has_finance:    hasFinance,
      has_plates:     hasPlates,
      has_transfer:   hasTransfer,
    });
  };

  submitGooglePayCharge = <TData extends object>(
    formId: string,
    walletToken: string,
    amount: string,
    plateFixtures: string = "",
    plateFixturesValue: number = 0
  ): Promise<TData> => {
    return this.client.post(`${this.resourceUrl}/googlepay-charge`, {
      formId,
      walletToken,
      amount,
      plateFixtures,
      plateFixturesValue,
    });
  };
}
